import React, { useEffect, useMemo, useState } from "react";
import { fetchJson } from "../../lib/api";
import { formatAmount, linkStatusStyle } from "../../lib/format-utils";
import type { BankAccount, BankTransaction, BankTransactionsResponse } from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../table";
import { AccountManagerModal } from "../accounts/account-manager-modal";
import { LinkModal } from "./link-modal";

export const BankTransactionsView = ({
  houseId,
  search,
  setSearch,
  navigate,
  houseRole,
}: {
  houseId: number | null;
  search: string;
  setSearch: (nextSearch: string, options?: { replace?: boolean }) => void;
  navigate: (path: string, search?: string) => void;
  houseRole: "readonly" | "full";
}) => {
  const { t } = useI18n();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const accountParam = Number.parseInt(searchParams.get("account") ?? "", 10);
  const selectedAccountId = Number.isNaN(accountParam) ? null : accountParam;
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [transactions, setTransactions] = useState<BankTransactionsResponse | null>(null);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isManagerOpen, setIsManagerOpen] = useState(false);
  const [activeTransaction, setActiveTransaction] = useState<BankTransaction | null>(null);
  const pageSize = 20;
  const amountEpsilon = 0.005;
  const isReadonly = houseRole === "readonly";

  const loadAccounts = async () => {
    if (!houseId) {
      setAccounts([]);
      return;
    }
    setIsLoadingAccounts(true);
    setError(null);
    try {
      const response = await fetchJson<{ accounts: BankAccount[] }>(
        `/api/houses/${houseId}/bank-accounts`,
      );
      setAccounts(response.accounts);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : t("bankTransactions.loadAccountsFailed", "Failed to load accounts"),
      );
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  useEffect(() => {
    void loadAccounts();
  }, [houseId]);

  useEffect(() => {
    if (!accounts.length) {
      return;
    }
    if (selectedAccountId && accounts.some((account) => account.id === selectedAccountId)) {
      return;
    }
    const nextParams = new URLSearchParams(search);
    nextParams.set("account", String(accounts[0].id));
    setSearch(`?${nextParams.toString()}`, { replace: true });
  }, [accounts, search, selectedAccountId, setSearch]);

  useEffect(() => {
    const selectedBankAccountId = accounts.some((account) => account.id === selectedAccountId)
      ? selectedAccountId
      : null;
    if (!houseId || !selectedBankAccountId) {
      setTransactions(null);
      return;
    }
    setIsLoadingTransactions(true);
    setError(null);
    fetchJson<BankTransactionsResponse>(
      `/api/houses/${houseId}/bank-transactions?account=${selectedBankAccountId}`,
    )
      .then((response) => {
        setTransactions(response);
      })
      .catch((fetchError) => {
        setError(
          fetchError instanceof Error
            ? fetchError.message
            : t("bankTransactions.loadFailed", "Failed to load transactions"),
        );
      })
      .finally(() => {
        setIsLoadingTransactions(false);
      });
  }, [accounts, houseId, search, searchParams, selectedAccountId, setSearch]);

  if (!houseId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("bankTransactions.title", "Bank transactions")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted">
            {t("bankTransactions.selectHouse", "Select a house to manage bank transactions.")}
          </p>
        </CardContent>
      </Card>
    );
  }

  const pageParam = Number.parseInt(searchParams.get("page") ?? "", 10);
  const currentPage = Number.isNaN(pageParam) ? 1 : pageParam;

  const updateAccount = (value: string) => {
    const nextParams = new URLSearchParams(search);
    nextParams.set("account", value);
    nextParams.set("page", "1");
    setSearch(`?${nextParams.toString()}`);
  };

  const selectedAccount = accounts.find((account) => account.id === selectedAccountId) ?? null;
  const selectedBankAccountId = selectedAccount?.id ?? null;
  const transactionRows = transactions?.transactions ?? [];
  const totalPages = Math.max(1, Math.ceil(transactionRows.length / pageSize));
  const safePage = Math.min(Math.max(currentPage, 1), totalPages);
  const pagedTransactions = transactionRows.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize,
  );
  const pageStart = transactionRows.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const pageEnd = Math.min(safePage * pageSize, transactionRows.length);

  const updatePage = (nextPage: number) => {
    const nextParams = new URLSearchParams(search);
    nextParams.set("page", String(nextPage));
    setSearch(`?${nextParams.toString()}`);
  };

  useEffect(() => {
    if (!transactions || transactionRows.length === 0) {
      return;
    }
    if (currentPage !== safePage) {
      const nextParams = new URLSearchParams(search);
      nextParams.set("page", String(safePage));
      setSearch(`?${nextParams.toString()}`, { replace: true });
    }
  }, [currentPage, safePage, search, setSearch, transactionRows.length, transactions]);

  return (
    <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-lg font-semibold text-ink">
            {t("bankTransactions.title", "Bank transactions")}
          </p>
          <p className="text-sm text-muted">
            {t("bankTransactions.subtitle", "Import and review bank statements.")}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div className="mr-0 w-full space-y-1 sm:mr-3 sm:w-auto">
            <label className="mr-3 inline-block text-sm font-medium text-ink" htmlFor="account-select">
              {t("bankTransactions.bankAccount", "Bank account")}
            </label>
            <select
              id="account-select"
              className="h-9 w-full rounded-md border px-3 text-sm brand-input brand-focus sm:min-w-[240px] md:min-w-[320px]"
              value={selectedBankAccountId ? String(selectedBankAccountId) : ""}
              onChange={(event) => updateAccount(event.target.value)}
              disabled={!accounts.length}
            >
              <option value="" disabled>
                {t("bankTransactions.selectBankAccount", "Select bank account")}
              </option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name} · {account.number} · {account.currency}
                </option>
              ))}
            </select>
          </div>
          <Button variant="outline" onClick={() => setIsManagerOpen(true)} disabled={isReadonly}>
            {t("bankTransactions.manageAccounts", "Manage bank accounts")}
          </Button>
          <Button
            onClick={() => navigate("/bank-transactions/import", search)}
            disabled={!selectedBankAccountId || isReadonly}
          >
            {t("bankTransactions.importTransactions", "Import transactions")}
          </Button>
        </div>
      </div>
      {isReadonly ? (
        <p className="rounded-md border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {t(
            "bankTransactions.readonly",
            "This house is read-only. Bank account changes and linking are disabled.",
          )}
        </p>
      ) : null}
      {isLoadingAccounts ? (
        <p className="text-sm text-muted">{t("bankTransactions.loadingAccounts", "Loading accounts...")}</p>
      ) : null}
      {isLoadingTransactions ? (
        <p className="text-sm text-muted">
          {t("bankTransactions.loadingTransactions", "Loading transactions...")}
        </p>
      ) : null}
      {error ? <p className="text-sm text-error">{error}</p> : null}
      <Card>
        <CardHeader>
          <CardTitle>{t("bankTransactions.transactions", "Transactions")}</CardTitle>
        </CardHeader>
        <CardContent>
          {!selectedAccount ? (
            <p className="text-sm text-muted">
              {t("bankTransactions.chooseAccount", "Choose an account to see transactions.")}
            </p>
          ) : !transactions || transactionRows.length === 0 ? (
            <p className="text-sm text-muted">
              {t("bankTransactions.empty", "No transactions available.")}
            </p>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted">
                <span>
                  {t("pagination.showing", "Showing {start}-{end} of {total}", {
                    start: pageStart,
                    end: pageEnd,
                    total: transactionRows.length,
                  })}
                </span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[120px] sm:min-w-[140px]">
                      {t("common.date", "Date")}
                    </TableHead>
                    <TableHead>{t("bankTransactions.description", "Description")}</TableHead>
                    <TableHead>{t("bankTransactions.amount", "Amount")}</TableHead>
                    <TableHead className="min-w-[110px]">
                      {t("bankTransactions.link", "Link")}
                    </TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedTransactions.map((transaction) => {
                    const linkedAmount = Math.abs(transaction.linkedAmount);
                    const totalAmount = Math.abs(transaction.amount);
                    const showLinkedAmount =
                      linkedAmount > amountEpsilon &&
                      Math.abs(linkedAmount - totalAmount) > amountEpsilon;
                    return (
                      <TableRow key={transaction.id}>
                      <TableCell>{transaction.transactionDate ?? "—"}</TableCell>
                      <TableCell>{transaction.description ?? "—"}</TableCell>
                      <TableCell className="min-w-[110px]">
                        {formatAmount(
                          transaction.amount,
                          transaction.currency ?? selectedAccount.currency,
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 text-xs">
                          <span
                            className={`inline-flex w-fit items-center rounded-full px-2 py-1 font-medium ${linkStatusStyle(
                              transaction.linkStatus,
                            )}`}
                          >
                            {t(`bankTransactions.linkStatus.${transaction.linkStatus}`, transaction.linkStatus)}
                          </span>
                          {showLinkedAmount ? (
                            <span className="text-muted">
                              {t("bankTransactions.linkedAmount", "Linked {amount}", {
                                amount: formatAmount(transaction.linkedAmount, transaction.currency),
                              })}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setActiveTransaction(transaction)}
                          disabled={isReadonly}
                        >
                          {t("bankTransactions.reconcile", "Reconcile")}
                        </Button>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="text-sm text-muted">
                  {t("pagination.pageOf", "Page {current} of {total}", {
                    current: safePage,
                    total: totalPages,
                  })}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updatePage(safePage - 1)}
                    disabled={safePage <= 1}
                  >
                    {t("pagination.previous", "Previous")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updatePage(safePage + 1)}
                    disabled={safePage >= totalPages}
                  >
                    {t("pagination.next", "Next")}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      {isManagerOpen ? (
        <AccountManagerModal
          houseId={houseId}
          accounts={accounts}
          onClose={() => setIsManagerOpen(false)}
          onRefresh={loadAccounts}
          isReadonly={isReadonly}
        />
      ) : null}
      {activeTransaction ? (
        <LinkModal
          houseId={houseId}
          transaction={activeTransaction}
          currencyFallback={selectedAccount?.currency ?? null}
          onClose={() => setActiveTransaction(null)}
          onUpdated={() => {
            void loadAccounts();
            if (selectedBankAccountId) {
              void fetchJson<BankTransactionsResponse>(
                `/api/houses/${houseId}/bank-transactions?account=${selectedBankAccountId}`,
              ).then((response) => setTransactions(response));
            }
          }}
          isReadonly={isReadonly}
        />
      ) : null}
    </div>
  );
};
